/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useEffect, useRef } from "react";

/**
 * Hook that calls a callback when provided value changes (regardless dependecies change)
 * Watched value is automatically added to dependencies, not need to provide it again.
 * Value is compared shallowly via "===".
 *
 * @param value Value
 * @param callback Function to be called on change
 */
export function useValueChange<TValue>(
	value: TValue,
	callback: (newValue: TValue) => void,
	deps: React.DependencyList = []
): void {
	const lastValue = useRef<TValue>(value);

	useEffect(() => {
		if (value !== lastValue.current) {
			lastValue.current = value;
			callback(value);
		}
	}, [ value, ...(deps ?? []) ]);
}
